import { SelectChangeEvent } from '@mui/material';
import { get, has, isEqual, trim } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@verantos-dev/core-auth';
import { UserRolesIdsEnum, OrganizationTypeEnum } from '@verantos-dev/core-enums';
import { useGetAllOrganizationsQuery } from '@verantos-dev/data-access-organizations';
import { UpdateUserRequest } from '@verantos-dev/data-access-users';
import {
  CreateUserLegacyData,
  useCreateUserMutation,
  UserInfo,
  useUpdateUserMutation,
} from '@verantos-dev/data-access-users';
import { LegacyResponseApplicationError } from '@verantos-dev/types-common';

import { createEditUserType } from '../components/UsersListCreateEditDialog';
import { DialogActionType, useUsersListDialogContext } from '../context/UsersListDialogContext';

export type editFields = {
  firstName: string;
  lastName: string;
  email: string;
  organization: string;
  roleDataAdmin: boolean;
  roleStudyAdmin: boolean;
  roleResearcher: boolean;
};

export const useUsersListCreateEditDialog = (
  dialogType: createEditUserType = 'createUser',
  userData?: UserInfo
) => {
  const { dispatch } = useUsersListDialogContext();
  const {
    user: { isAdmin, organizationId: userOrgId, organizationName: userOrgName },
  } = useAuth();

  const [firstName, setFirstName] = useState(userData?.firstName || '');
  const [lastName, setLastName] = useState(userData?.lastName || '');
  const [email, setEmail] = useState(userData?.email || '');
  // Organization states
  const [organizationId, setOrganizationId] = useState(userData?.organizationId || '');
  const [organizationName, setOrganizationName] = useState(userData?.organizationName || '');
  const [organizationType, setOrganizationType] = useState('');
  // Role states
  const [roles, setRoles] = useState<UserRolesIdsEnum[]>(
    (userData?.roles as UserRolesIdsEnum[]) || []
  );
  // custom app error state
  const [appError, setAppError] = useState<LegacyResponseApplicationError[] | undefined>(undefined);
  const [emailError, setEmailError] = useState(false);

  const { data: organizationsData } = useGetAllOrganizationsQuery('');

  let displayOrganizationDropdown = false;
  if (userOrgName?.toLowerCase() === 'verantos') {
    displayOrganizationDropdown = true;
  }

  // in the case of a non-verantos user, set the organization to the user's organization
  useEffect(() => {
    if (!displayOrganizationDropdown) {
      const org = organizationsData?.data?.find((org) => org.organizationId === userOrgId);
      if (org) {
        setOrganizationId(org.organizationId);
        setOrganizationName(org.displayName);
        setOrganizationType(org.type);
      }
    }
  }, [organizationsData, userOrgId, displayOrganizationDropdown]);

  // input handlers
  const onChangeFirstName = (_: string, value: string) => {
    setFirstName(value);
  };

  const onChangeLastName = (_: string, value: string) => {
    setLastName(value);
  };

  const onChangeEmail = (_: string, value: string) => {
    const trimedValue = value.trim();

    setEmail(trimedValue);

    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    const valid = emailRegex.test(trimedValue.toLowerCase());
    setEmailError(!valid || trimedValue === '');
  };

  // Role handlers =========
  const onChangeRole = (selectedRole: string, checked: boolean) => {
    if (checked) {
      setRoles((prevRoles) => [...prevRoles, selectedRole as UserRolesIdsEnum]);
    } else {
      setRoles((prevRoles) => prevRoles.filter((role) => role !== selectedRole));
    }
  };

  // Organizations handlers =========
  useEffect(() => {
    if (organizationsData?.data && dialogType === 'editUser' && userData) {
      const organization = organizationsData?.data.find(
        (org) => org.organizationId === userData.organizationId
      );
      if (organization) {
        setOrganizationType(organization.type);
        setOrganizationName(organization.name);
      }
    }
  }, [dialogType, organizationsData, userData]);

  // take organization data and convert to dropdown options
  const organizationOptions = organizationsData?.data?.map((org) => {
    return {
      value: org.organizationId,
      displayValue: org.displayName,
    };
  });

  const onChangeOrganization = (event: SelectChangeEvent<string>) => {
    setOrganizationId(event.target.value);
    // find organization type
    const org = organizationsData?.data?.find((org) => org.organizationId === event.target.value);
    if (org) {
      setOrganizationType(org.type);
      setOrganizationName(org?.displayName || '');
      setRoles([]);
    }
  };

  // see if valid to release
  const validToCreateEdit = () => {
    if (dialogType === 'editUser' && userData) {
      return (
        !emailError &&
        trim(email) !== '' &&
        trim(firstName) !== '' &&
        trim(lastName) !== '' &&
        organizationId !== '' &&
        (organizationType === OrganizationTypeEnum.RESEARCH_PARTNER || roles.length > 0) &&
        (trim(email) !== userData.email ||
          trim(firstName) !== userData.firstName ||
          trim(lastName) !== userData.lastName ||
          organizationId !== userData.organizationId ||
          !isEqual(
            roles,
            userData.roles || organizationType === OrganizationTypeEnum.RESEARCH_PARTNER
          ))
      );
    }

    // for create a user
    return (
      !emailError &&
      trim(email) !== '' &&
      trim(firstName) !== '' &&
      trim(lastName) !== '' &&
      organizationId !== '' &&
      (organizationType === OrganizationTypeEnum.RESEARCH_PARTNER || roles.length > 0)
    );
  };

  // button handlers
  const onCancel = () => {
    dispatch({ type: DialogActionType.CLOSE_DIALOG });
  };

  // User creation mutation hook
  const [createUser, { isLoading: isCreateUserLoading, error: createUserError }] =
    useCreateUserMutation();

  // User update user mutation hook
  const [updateUser, { isLoading: isUpdateUserLoading, error: updateUserError }] =
    useUpdateUserMutation();

  // handle form submission
  const onSubmit = async () => {
    // make sure the data is valid
    if (!validToCreateEdit()) {
      return;
    }

    if (dialogType === 'editUser' && userData) {
      const data: UpdateUserRequest['data'] = {
        firstName,
        lastName,
        email,
        organizationId,
        organizationName,
        roles,
      };

      try {
        const result = await updateUser({
          userId: userData.userId,
          data,
        });
        if (result.data?.errors) {
          setAppError(result.data.errors);
          return;
        }
      } catch (error) {
        console.error('Error updating user', error);
      }
    } else if (dialogType === 'createUser') {
      // bundle up the data
      const data: CreateUserLegacyData = {
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || '',
        phone: '',
        organizationId: organizationId || '',
        organizationName: organizationName,
        roles,
        permissions: [],
      };

      try {
        const result = await createUser(data);
        if (result.data?.errors) {
          //setAppError(result.data.errors);
          return;
        }
      } catch (error) {
        console.error('Error creating user', error);
      }
    }
  };

  const apiErrorCode: number | undefined = useMemo(() => {
    if (has(createUserError, 'data.error_code')) {
      return get(createUserError, 'data.error_code');
    } else if (has(updateUserError, 'data.error_code')) {
      return get(updateUserError, 'data.error_code');
    }
    return undefined;
  }, [createUserError, updateUserError]);

  return {
    firstName,
    lastName,
    email,
    organizationId,
    roles,

    onChangeFirstName,
    onChangeLastName,
    onChangeEmail,
    onChangeOrganization,
    onChangeRole,

    isApiLoading: isCreateUserLoading || isUpdateUserLoading,
    apiError: appError || createUserError || updateUserError,
    apiErrorCode,

    emailError,
    organizationOptions,
    organizationType,
    organizationName,
    displayOrganizationDropdown,

    validToCreateEdit: validToCreateEdit(),

    isOrResearcherChecked: roles.includes(UserRolesIdsEnum.OR_RESEARCHER),
    isOrDataAdminChecked: roles.includes(UserRolesIdsEnum.VRN_DATA_ADMIN),
    isSaAdminChecked: roles.includes(UserRolesIdsEnum.SA_ADMIN),

    onCancel,
    onSubmit,

    isLoggedInUserSystemAdmin: isAdmin,
  };
};
